import { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { RiExternalLinkLine } from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import { ButtonIcon } from '@spotify-internal/encore-web';
import {
  MODULE_STYLES,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  DefaultArtifactListItem,
  createArtifactExtension,
} from '@spotify/plugin-confidence-artifacts-react';

import { confidencePulsePlugin } from '../plugin';
import { pulsesPageRouteRef } from '../routes';

const usePulseHref = (uri: string): string | undefined => {
  const pulsesPage = useRouteRef(pulsesPageRouteRef);
  const resourceName = uri.replace(/^\/\//, '');
  const pulseId = extractLastNameComponent(resourceName);
  if (!pulsesPage || !pulseId) return undefined;
  return `${pulsesPage()}/${pulseId}`;
};

const isPulseArtifact = (artifact: AttachmentFragment): boolean => {
  const resourceName = artifact.uri.replace(/^\/\//, '');
  return isResourceName(resourceName, 'pulses');
};

const PulseGoToButton = ({ artifact }: { artifact: AttachmentFragment }) => {
  const href = usePulseHref(artifact.uri);

  if (!href) {
    return null;
  }

  return (
    <ButtonIcon
      variant="tertiary"
      size="small"
      onClick={() => window.open(href, '_blank')}
      aria-label="Go to pulse"
    >
      <RiExternalLinkLine size={16} />
    </ButtonIcon>
  );
};

const PulseArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
}) => {
  const href = usePulseHref(artifact.uri);
  const navigate = useNavigate();
  const handleSelect = href ? () => navigate(href) : onSelect;

  return (
    <DefaultArtifactListItem
      artifact={artifact}
      icon={icon}
      href={href}
      onSelect={handleSelect}
      onDelete={onDelete}
    />
  );
};

type ArtifactIconComponent = ComponentType<{
  size?: number | string;
  className?: string;
  style?: object;
}>;

const pulseIcon = {
  Icon: MODULE_STYLES.pulses.Icon as ArtifactIconComponent,
  color: MODULE_STYLES.pulses.color,
};

export const PulseArtifactExtension = confidencePulsePlugin.provide(
  createArtifactExtension({
    id: 'pulse',
    predicate: isPulseArtifact,
    icon: () => pulseIcon,
    label: 'Pulse',
    category: 'Resources',
    renderHeaderActions: artifact => <PulseGoToButton artifact={artifact} />,
    render: () => null,
    renderListItem: (artifact, { onSelect, onDelete, icon }) => (
      <PulseArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    ),
  }),
);
